import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Header } from "@/components/portfolio/Header";
import { getCaseStudy, caseStudies } from "@/data/caseStudies";

export default function CaseStudy() {
  const { slug } = useParams();
  const study = getCaseStudy(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!study) return <Navigate to="/" replace />;

  const currentIndex = caseStudies.findIndex((c) => c.slug === slug);
  const next = caseStudies[(currentIndex + 1) % caseStudies.length];

  return (
    <div data-testid="case-study-page" className="min-h-screen bg-[#F4F4F2] text-[#0A0A0A]">
      <Header />

      {/* Title block */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="case-back-link"
            className="inline-flex items-center gap-2 font-mono-mini text-[#0A0A0A] mb-10 md:mb-14 group"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
            <span className="link-underline">Index</span>
          </Link>

          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">{study.index}</div>
              <div className="font-mono-mini text-[#0A0A0A] mt-1">{study.category}</div>
            </div>
            <div className="col-span-12 md:col-span-9">
              <motion.h1
                data-testid="case-study-title"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
                className="font-display text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.95] tracking-tighter"
              >
                {study.title}
              </motion.h1>
            </div>
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="px-6 md:px-12">
        <div className="max-w-[1500px] mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.65, 0, 0.35, 1] }}
            data-testid="case-cover"
            className="relative h-[55vh] md:h-[80vh] overflow-hidden"
            style={{ backgroundColor: study.accent || "#EFEEEC" }}
          >
            <img
              src={study.cover}
              alt={study.title}
              className={`absolute inset-0 w-full h-full ${
                study.coverFit === "contain" ? "object-contain" : "object-cover"
              }`}
            />
          </motion.div>
        </div>
      </section>

      {/* Metadata grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-5 border-t border-black/15">
            {[
              ["Client", study.client],
              ["Role", study.role],
              ["Timeline", study.timeline],
              ["Year", study.year],
              ["Deliverables", study.deliverables.join(" · ")],
            ].map(([k, v], i) => (
              <div
                key={k}
                className={`py-6 md:py-8 px-1 border-b border-black/15 ${
                  i < 4 ? "md:border-r md:border-black/15" : ""
                } ${i % 2 === 0 ? "border-r border-black/15 md:border-r" : ""}`}
              >
                <div className="font-mono-mini text-[#8A8A8A]">{k}</div>
                <div className="mt-3 text-sm md:text-base font-medium leading-snug">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-3">
            <div className="font-mono-mini text-[#8A8A8A]">Overview</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <p className="font-display text-2xl md:text-4xl lg:text-[2.6rem] leading-[1.15] tracking-tighter">
              {study.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Problem / Process / Solution */}
      {[
        ["Problem", study.problem],
        ["Process", study.process],
        ["Solution", study.solution],
      ].map(([label, body]) => (
        <section key={label} className="py-12 md:py-16 border-t border-black/10">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">{label}</div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <p className="text-lg md:text-xl leading-relaxed text-[#0A0A0A]">{body}</p>
            </div>
          </div>
        </section>
      ))}

      {/* Optional: Target Audience + Competitive Context (two-up) */}
      {(study.targetAudience || study.competitiveContext) && (
        <section className="py-12 md:py-20 border-t border-black/10">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">Context</div>
            </div>
            <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {study.targetAudience && (
                <div data-testid="case-target-audience" className="border-t border-black/15 pt-6">
                  <div className="font-mono-mini text-[#8A8A8A] mb-3">Target Audience</div>
                  <p className="text-base md:text-lg leading-relaxed">{study.targetAudience}</p>
                </div>
              )}
              {study.competitiveContext && (
                <div data-testid="case-competitive" className="border-t border-black/15 pt-6">
                  <div className="font-mono-mini text-[#8A8A8A] mb-3">Competitive Landscape</div>
                  <p className="text-base md:text-lg leading-relaxed">{study.competitiveContext}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Optional: Key Screens */}
      {study.keyScreens && study.keyScreens.length > 0 && (
        <section className="py-12 md:py-20 border-t border-black/10">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">Key Screens</div>
            </div>
            <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              {study.keyScreens.map((s, i) => (
                <div
                  key={s.title}
                  data-testid={`case-key-screen-${i}`}
                  className="border-t border-black/15 pt-6"
                >
                  <div className="font-mono-mini text-[#8A8A8A] mb-3">
                    {String(i + 1).padStart(2, "0")} / {s.title}
                  </div>
                  <p className="text-base md:text-lg leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional: Challenges + Learnings */}
      {(study.challenges || study.learnings) && (
        <section className="py-12 md:py-20 border-t border-black/10">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">Reflection</div>
            </div>
            <div className="col-span-12 md:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              {study.challenges && (
                <div data-testid="case-challenges" className="border-t border-black/15 pt-6">
                  <div className="font-mono-mini text-[#8A8A8A] mb-3">Challenges</div>
                  <p className="text-base md:text-lg leading-relaxed">{study.challenges}</p>
                </div>
              )}
              {study.learnings && (
                <div data-testid="case-learnings" className="border-t border-black/15 pt-6">
                  <div className="font-mono-mini text-[#8A8A8A] mb-3">Learnings</div>
                  <p className="text-base md:text-lg leading-relaxed">{study.learnings}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-[1500px] mx-auto grid grid-cols-12 gap-6 md:gap-12">
          {study.gallery.map((item, i) => {
            const src = typeof item === "string" ? item : item.src;
            const fit = typeof item === "string" ? "cover" : item.fit || "cover";
            const bg = typeof item === "string" ? "#EFEEEC" : item.bg || "#EFEEEC";
            const pos = typeof item === "string" ? "center" : item.position || "center";
            return (
              <div
                key={i}
                className={`col-span-12 ${
                  i % 2 === 0 ? "md:col-span-8" : "md:col-span-4"
                } overflow-hidden h-[50vh] md:h-[75vh] relative`}
                style={{ backgroundColor: bg }}
              >
                <img
                  src={src}
                  alt=""
                  style={{ objectPosition: pos }}
                  className={`absolute inset-0 w-full h-full ${
                    fit === "contain" ? "object-contain" : "object-cover"
                  }`}
                />
              </div>
            );
          })}
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-16 md:py-24 border-t border-black/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10">
            <div className="col-span-12 md:col-span-3">
              <div className="font-mono-mini text-[#8A8A8A]">Outcomes</div>
            </div>
            <div className="col-span-12 md:col-span-8">
              <p className="text-lg md:text-xl leading-relaxed max-w-2xl">
                The shipped product, measured by what it changed.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-black/15">
            {study.outcomes.map((o, i) => (
              <div
                key={i}
                className={`py-10 md:py-14 ${
                  i < study.outcomes.length - 1 ? "md:border-r border-black/15" : ""
                } border-b md:border-b-0 border-black/15`}
              >
                <div className="font-display text-6xl md:text-7xl tracking-tighter leading-none">
                  {o.metric}
                </div>
                <div className="font-mono-mini text-[#8A8A8A] mt-4">{o.label}</div>
              </div>
            ))}
          </div>

          {study.externalLink && (
            <a
              href={study.externalLink.href}
              data-testid="case-external-link"
              target="_blank"
              rel="noreferrer"
              className="group mt-12 md:mt-16 inline-flex items-center gap-3 border border-black/15 px-6 py-4 hover:bg-[#0A0A0A] hover:text-[#F4F4F2] transition-colors duration-300"
            >
              <span className="font-mono-mini">{study.externalLink.label}</span>
              <ArrowUpRight
                size={18}
                className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
          )}
        </div>
      </section>

      {/* Next project */}
      <section className="border-t border-black/10 bg-[#0A0A0A] text-[#F4F4F2]">
        <Link
          to={`/work/${next.slug}`}
          data-testid="case-next-link"
          className="block py-24 md:py-32 group"
        >
          <div className="max-w-[1500px] mx-auto px-6 md:px-12">
            <div className="font-mono-mini text-[#8A8A8A] mb-6">Next project</div>
            <div className="flex items-baseline gap-4 md:gap-8">
              <span className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter transition-transform duration-700 group-hover:translate-x-3">
                {next.title}
              </span>
              <ArrowUpRight
                size={48}
                strokeWidth={1.5}
                className="text-[#FF3B30] transition-transform duration-700 group-hover:translate-x-2 group-hover:-translate-y-2"
              />
            </div>
          </div>
        </Link>
      </section>

      {/* Footer mini */}
      <footer className="bg-[#0A0A0A] text-[#8A8A8A] border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 font-mono-mini">
          <span>© 2026 Mrinmoy Mondal</span>
          <Link to="/" data-testid="case-footer-home" className="link-underline">
            Return to index
          </Link>
        </div>
      </footer>
    </div>
  );
}
